
import Vue, { PropType } from "vue";

import SearchIcon from "@/components/icons/SearchIcon.vue";
import DateTimeRange from "@/components/DateTimeRange.vue";
import { RvsCalculationJob, ThresholdFmFilesResponse, ThresholdFmRow } from "@/clients/RvsCalculationClient";

const VALID_AXES = ["H", "A", "V"];

export default Vue.extend({
  name: "ThresholdFmTab",
  components: { SearchIcon, DateTimeRange },
  props: {
    activeJob: { type: Object as PropType<RvsCalculationJob | null>, default: null },
    jobBusy: { type: Boolean, default: false },
  },
  data() {
    return {
      selectedSensorId: null as string | null,
      sensorSearch: null as string | null,
      sensorOptions: [] as string[],
      isLoadingSensors: false,
      debounce: 0 as any,
      dateFrom: null as string | null,
      dateTo: null as string | null,
      dateFilterKey: 0,
      filesPreview: null as ThresholdFmFilesResponse | null,
      isLoadingFiles: false,
      thresholdExists: false,
      lastModified: null as string | null,
      rows: [] as ThresholdFmRow[],
      editRows: [] as ThresholdFmRow[],
      paramSearch: "",
      axisFilter: "" as "" | "V" | "H" | "A", // "" = All
      editMode: false,
      isLoadingThreshold: false,
      isSaving: false,
      isCreatingJob: false,
      recalcOpen: false,
      confirmSaveOpen: false,
    };
  },
  computed: {
    displayRows(): ThresholdFmRow[] {
      return this.editMode ? this.editRows : this.rows;
    },
    visibleRows(): ThresholdFmRow[] {
      const search = this.paramSearch?.trim().toLowerCase();
      const axis = this.axisFilter;
      return this.displayRows.filter((row: ThresholdFmRow) => {
        if (axis && (row.axis || "").toUpperCase() !== axis) return false;
        if (search && !(row.parameterType || "").toLowerCase().includes(search)) return false;
        return true;
      });
    },
    canCalculate(): boolean {
      return (
        !!this.selectedSensorId && !!this.filesPreview && this.filesPreview.count > 0 && !this.jobBusy && !this.isCreatingJob
      );
    },
    editError(): string | null {
      // Mirror the backend PUT validation so Save is blocked before the request.
      const rows = this.editRows;
      if (!rows.length) return "At least one row is required.";
      const seen = new Set<string>();
      for (const row of rows) {
        if (!VALID_AXES.includes((row.axis || "").toUpperCase())) return `Axis must be one of ${VALID_AXES.join(", ")}.`;
        if (row.wn === null || row.al === null || isNaN(row.wn as any) || isNaN(row.al as any))
          return "Warning and Alarm must be numbers.";
        // Signed parameters (kurtosis, skewness, ...) can have negative thresholds
        // in either order, so no sign or wn/al ordering rule is applied here.
        const key = `${row.parameterType}||${row.axis}`;
        if (seen.has(key)) return "Duplicate (parameter, axis) rows are not allowed.";
        seen.add(key);
      }
      return null;
    },
  },
  created() {
    this.fetchSensorOptions();
  },
  beforeDestroy() {
    clearTimeout(this.debounce);
  },
  methods: {
    async fetchSensorOptions(search?: string) {
      try {
        this.isLoadingSensors = true;
        this.sensorOptions = await (this as any).$dep.rvsCalculationService.getSensorIds(search || undefined);
      } catch {
        this.sensorOptions = [];
      } finally {
        this.isLoadingSensors = false;
      }
    },
    onSensorChange() {
      this.paramSearch = "";
      this.axisFilter = "";
      this.editMode = false;
      this.editRows = [];
      this.loadThreshold();
      if (this.dateFrom || this.dateTo) this.loadFiles();
      else this.filesPreview = null;
    },
    openRecalc() {
      // Start the popup with a clean baseline range each time.
      this.dateFrom = null;
      this.dateTo = null;
      this.filesPreview = null;
      this.dateFilterKey += 1; // remount DateTimeRange to clear its picker
      this.recalcOpen = true;
    },
    async loadThreshold() {
      if (!this.selectedSensorId) return;
      try {
        this.isLoadingThreshold = true;
        const result = await (this as any).$dep.rvsCalculationService.getThresholdFm(this.selectedSensorId);
        this.thresholdExists = result.exists;
        this.lastModified = result.lastModified;
        this.rows = result.rows || [];
      } catch {
        this.thresholdExists = false;
        this.rows = [];
        this.lastModified = null;
      } finally {
        this.isLoadingThreshold = false;
      }
    },
    onDateRangeSubmit(range: { startDate: string; endDate: string }) {
      // Strip the literal ".000Z"; the backend compares naive UTC+7 datetimes.
      this.dateFrom = range.startDate.replace(".000Z", "");
      this.dateTo = range.endDate.replace(".000Z", "");
      this.loadFiles();
    },
    async loadFiles() {
      if (!this.selectedSensorId) return;
      try {
        this.isLoadingFiles = true;
        this.filesPreview = await (this as any).$dep.rvsCalculationService.getThresholdFmFiles(
          this.selectedSensorId,
          this.dateFrom || undefined,
          this.dateTo || undefined
        );
      } catch {
        this.filesPreview = { count: 0, firstFile: null, lastFile: null };
      } finally {
        this.isLoadingFiles = false;
      }
    },
    async calculate() {
      if (!this.selectedSensorId || !this.dateFrom || !this.dateTo) return;
      try {
        this.isCreatingJob = true;
        const { jobId } = await (this as any).$dep.rvsCalculationService.createThresholdFmJob(
          this.selectedSensorId,
          this.dateFrom,
          this.dateTo
        );
        this.recalcOpen = false;
        this.$store.dispatch("openAlertMessage", {
          open: true,
          message: "Threshold recalculation started — you can leave this page",
          type: "success",
        });
        this.$emit("job-created", jobId);
      } catch (error: any) {
        this.$store.dispatch("openAlertMessage", {
          open: true,
          message: error?.response?.data?.detail || "error: failed to start threshold recalculation",
          type: "error",
        });
      } finally {
        this.isCreatingJob = false;
      }
    },
    startEdit() {
      this.editRows = this.rows.map((row) => ({ ...row }));
      this.editMode = true;
    },
    cancelEdit() {
      this.editMode = false;
      this.editRows = [];
    },
    async save() {
      if (!this.selectedSensorId || this.editError) return;
      try {
        this.isSaving = true;
        const result = await (this as any).$dep.rvsCalculationService.updateThresholdFm(
          this.selectedSensorId,
          this.editRows
        );
        this.thresholdExists = result.exists;
        this.lastModified = result.lastModified;
        this.rows = result.rows || [];
        this.editMode = false;
        this.editRows = [];
        this.confirmSaveOpen = false;
        this.$store.dispatch("openAlertMessage", {
          open: true,
          message: "Threshold file updated",
          type: "success",
        });
      } catch (error: any) {
        this.$store.dispatch("openAlertMessage", {
          open: true,
          message: error?.response?.data?.detail || "error: failed to save threshold file",
          type: "error",
        });
      } finally {
        this.isSaving = false;
      }
    },
    formatValue(value: number): string {
      if (value === 0) return "0";
      return Number(value).toFixed(5);
    },
    formatTimestamp(timestamp: string | null | undefined) {
      if (!timestamp) return "-";
      return (this as any).$date(timestamp).format("DD/MM/YYYY HH:mm");
    },
  },
  watch: {
    sensorSearch(search: string | null) {
      if (search !== null && search === this.selectedSensorId) return;
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => this.fetchSensorOptions(search || undefined), 600);
    },
    activeJob(job: RvsCalculationJob | null, previous: RvsCalculationJob | null) {
      // When a threshold job for the current sensor finishes, reload the table
      // and the file preview so the freshly written values show.
      const finished = previous?.status === "running" && job && job.status !== "running";
      if (finished && job?.type === "threshold_fm" && job?.sensorId === this.selectedSensorId) {
        this.loadThreshold();
        if (this.dateFrom || this.dateTo) this.loadFiles();
      }
    },
  },
});
